import {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMe} from '../../../common/hooks/useMe'
import {hasCreativeContent, type CreativeAssetState} from '../../../common/utils/creativeAssetRenderer'
import {
  generateCreativeVideo,
  resolveVideoOutput,
  VideoCancelledError,
  VideoEncodingUnsupportedError,
  type CreativeVideoOptions,
  type CreativeVideoSettings,
} from '../../../common/utils/creativeVideoRenderer'
import {getPlacementSpec, type CreativePlacementId} from '../../../common/constants/creativeAssetSpecs'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {reportError} from '../../../common/errors/reportError'
import {downloadFile} from '../../../common/utils/downloadFile'
import {estimateRemainingSeconds, formatRemaining} from '../../../common/utils/estimateRemaining'
import ConverterExportDialog from '../converterShared/ConverterExportDialog'
import {CONVERTER_AD_FEATS} from '../converterShared/adFeatIcons'

interface CreativeVideoPanelProps {
  state: CreativeAssetState
  placement: CreativePlacementId
  settings: CreativeVideoSettings
}

/** Everything one render needs, frozen at the moment the user started it. */
interface VideoJob {
  state: CreativeAssetState
  options: CreativeVideoOptions
}

interface OutputSize {
  width: number
  height: number
}

/** Above this many frames a 4K export takes minutes on ordinary hardware. */
const HEAVY_FRAME_COUNT = 900

/** Renders a job; a cancel is tracked the same way whether it came from the button or the closed dialog. */
const renderVideo = async (
  {state, options}: VideoJob,
  reportProgress: (value: number) => void,
  signal?: AbortSignal,
): Promise<Blob> => {
  const startedAt = performance.now()
  let lastPercent = 0
  try {
    return await generateCreativeVideo(
      state,
      options,
      (value) => {
        lastPercent = value
        reportProgress(value)
      },
      signal,
    )
  } catch (err) {
    if (err instanceof VideoCancelledError) {
      trackEvent('creative_assets_video_cancelled', {
        percent: Math.round(lastPercent),
        elapsed_sec: Math.round((performance.now() - startedAt) / 1000),
        placement: options.placement,
        duration_sec: options.durationSec,
        fps: options.fps,
      })
    }
    throw err
  }
}

const CreativeVideoPanel = ({state, placement, settings}: CreativeVideoPanelProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [busy, setBusy] = useState(false)
  const [percent, setPercent] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Set when the upsell dialog opens: later edits must not restart the render running inside it.
  const [dialogJob, setDialogJob] = useState<VideoJob | null>(null)
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null)
  const [output, setOutput] = useState<OutputSize | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const spec = getPlacementSpec(placement)
  const hasCredits = me !== null && me.credits > 0
  const ready = hasCreativeContent(state)
  const heavy = settings.durationSec * settings.fps > HEAVY_FRAME_COUNT
  const size = output ?? spec
  const filename = `${spec.fileBase}-video-${size.width}x${size.height}.mp4`

  // The size the encoder will really use — Search Results falls back to 1920 × 1280 when 4K is unsupported.
  useEffect(() => {
    let cancelled = false
    resolveVideoOutput(placement, settings.fps)
      .then((resolved) => {
        if (!cancelled) setOutput({width: resolved.width, height: resolved.height})
      })
      .catch(() => {
        if (!cancelled) setOutput(null)
      })
    return () => {
      cancelled = true
    }
  }, [placement, settings.fps])

  // Leaving video mode unmounts this panel: stop a credited render instead of encoding for minutes unseen.
  useEffect(() => () => abortRef.current?.abort(), [])

  const handleCancel = useCallback(() => abortRef.current?.abort(), [])

  const handleClick = useCallback(async () => {
    if (!ready) return
    const job: VideoJob = {state, options: {placement, ...settings}}
    trackEvent('creative_assets_video_download_clicked', {
      has_credits: hasCredits,
      placement,
      bg_type: settings.bg.type,
      content_type: settings.content.type,
      duration_sec: settings.durationSec,
      fps: settings.fps,
    })
    if (!hasCredits) {
      setDialogJob(job)
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    const startedAt = performance.now()
    trackEvent('creative_assets_video_started', {gated: false})
    setBusy(true)
    setPercent(0)
    setEtaSeconds(null)
    setError(null)
    try {
      const blob = await renderVideo(
        job,
        (value) => {
          setPercent(value)
          setEtaSeconds(estimateRemainingSeconds(performance.now() - startedAt, value))
        },
        controller.signal,
      )
      await downloadFile(blob, filename)
      trackEvent('creative_assets_video_completed', {size_bytes: blob.size, gated: false})
    } catch (err) {
      if (err instanceof VideoCancelledError) return
      if (!(err instanceof VideoEncodingUnsupportedError)) reportError(err, 'creative-assets-video')
      setError(
        t(
          err instanceof VideoEncodingUnsupportedError
            ? 'creativeAssets.video.unsupported'
            : 'creativeAssets.download.failed',
        ),
      )
    } finally {
      abortRef.current = null
      setBusy(false)
    }
  }, [ready, state, placement, settings, hasCredits, filename, t])

  const handleDialogGenerate = useCallback(
    (reportProgress: (value: number) => void, signal: AbortSignal) =>
      dialogJob ? renderVideo(dialogJob, reportProgress, signal) : Promise.reject(new Error('no video job')),
    [dialogJob],
  )
  const handleDialogClose = useCallback(() => setDialogJob(null), [])

  return (
    <div className="caa-download">
      <p className="cvt-hint">
        {t('creativeAssets.video.spec', {
          width: size.width,
          height: size.height,
          seconds: settings.durationSec,
          fps: settings.fps,
        })}
      </p>

      <button type="button" className="cvt-btn cvt-btn-primary" onClick={handleClick} disabled={busy || !ready}>
        {busy ? t('creativeAssets.video.busy', {percent: Math.round(percent)}) : t('creativeAssets.video.cta')}
      </button>
      {busy && (
        <>
          {etaSeconds !== null && (
            <p className="cvt-hint">{t('creativeAssets.dialog.eta', {time: formatRemaining(etaSeconds)})}</p>
          )}
          <button type="button" className="cvt-btn" onClick={handleCancel}>
            {t('creativeAssets.dialog.cancel')}
          </button>
        </>
      )}
      {error && <p className="cvt-error">{error}</p>}
      {!ready && <p className="cvt-hint">{t('creativeAssets.download.needContent')}</p>}
      {heavy && <p className="cvt-hint">{t('creativeAssets.video.heavyHint')}</p>}
      <p className="cvt-hint">{t('creativeAssets.video.browserHint')}</p>

      {dialogJob && (
        <ConverterExportDialog
          namespace="creativeAssets"
          breadcrumbCategory="creative-assets-video"
          analyticsSurface="creative_assets_video"
          startedEvent="creative_assets_video_started"
          completedEvent="creative_assets_video_completed"
          progressMode="real"
          cancellable
          filename={filename}
          showAd
          adFeats={CONVERTER_AD_FEATS}
          adCtaTo="/free"
          onGenerate={handleDialogGenerate}
          onClose={handleDialogClose}
        />
      )}
    </div>
  )
}

export default CreativeVideoPanel
